import { gql, useLazyQuery, useSubscription } from "@apollo/client";
import { Card, Collapse, Fade, Grid, Icon, Modal, Slide, Tooltip } from "@mui/material";
import logo from "assets/images/comedor.png";

import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto, makeNiceText } from "utils/formatters";
import { getPedidoPaymentInfo } from "utils/pedidoPayment";
import { obtenerErrorQrPedido } from "utils/qrPedido";
import ModalCargarComprobante from "../ModalCargarComprobante";
import ModalInfo from "./modalInfo";
import ReservaVencidaAviso from "./ReservaVencidaAviso";
const foodIcons = {
  Entrada: "restaurant",
  "Desayuno / Merienda": "emoji_food_beverage",
  "Plato principal": "lunch_dining",
  Postre: "icecream",
  Ensaladas: "rice_bowl",
  "Bebidas sin alcohol": "water_drop_outlined",
  "Bebidas con alcohol": "local_bar",
};

const getFoodIcon = (categoria) => {
  return foodIcons[categoria] || "restaurant";
};

const GENERATE_QR = gql`
  query generateQR($token: String!, $idPedido: ID!) {
    generateQR(token: $token, idPedido: $idPedido) {
      token
      vigenciaMinutos
    }
  }
`;

// Mismo criterio que la API: el QR sirve para preparar o entregar el pedido y, si está
// reservado, desde el horario de reserva.
const esperaHorarioReserva = (pedido) =>
  pedido?.estado === "RESERVADO" && dayjs().isBefore(dayjs(pedido?.horaReserva));

const puedeGenerarQr = (pedido) =>
  (pedido?.estado === "RESERVADO" && !esperaHorarioReserva(pedido)) ||
  pedido?.estado === "EN PREPARACIÓN";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "40%", xxl: "30%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalDetallesPedido({ open, handleClose, idPedido, token, refetch }) {
  const [pedido, setPedido] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);
  const [openModalCargarComprobante, setOpenModalCargarComprobante] = useState(false);
  const [showContent, setShowContent] = useState(false);
  // { token, vigenciaMinutos, venceEn }: venceEn se calcula con el reloj del dispositivo.
  const [qr, setQr] = useState(null);
  const [qrVencido, setQrVencido] = useState(false);
  const [qrError, setQrError] = useState(null);

  const getVoucherNombre = (venta) =>
    venta?.voucher?.grupo?.nombre || venta?.voucher?.nombreVoucher || "Beneficio/Voucher";

  const getVoucherTipo = (venta) => venta?.tipo || venta?.voucher?.tipo || null;

  const getVoucherTipoLabel = (venta) => {
    const tipo = getVoucherTipo(venta);
    if (tipo === "MONTO") return "Monto";
    if (tipo === "PRODUCTO") return "Producto";
    if (tipo === "CATEGORIA") return "Categoría";
    return tipo;
  };

  const getVoucherDestino = (venta) => {
    if (getVoucherTipo(venta) === "PRODUCTO") return venta?.voucher?.producto?.nombre || null;
    if (getVoucherTipo(venta) === "CATEGORIA") return venta?.voucher?.categoria?.categoria || null;
    return null;
  };

  const formatVoucherCantidad = (venta) => {
    const tipoVoucher = getVoucherTipo(venta);
    const cantidad = Number(venta?.cantidad || 0);
    if (!cantidad) return null;
    if (tipoVoucher === "MONTO") return `$ ${formatMoneyPunto(cantidad)}`;
    return `${cantidad.toLocaleString("es-AR")} ${cantidad === 1 ? "unidad" : "unidades"}`;
  };

  const [getPedido, { loading }] = useLazyQuery(
    gql`
      query pedido($id: ID!, $token: String) {
        pedido(id: $id, token: $token) {
          createdAt
          id
          comentario
          tipo
          paraRetirar
          fechaPedido
          total
          estado
          cuentaCorriente
          pagoSplitCC
          turnoReserva
          horaReserva
          venta {
            id
            url
            idVoucher
            cantidad
            tipo
            subtotal
            descuento
            recargo
            total
            voucher {
              id
              tipo
              idProducto
              nombreVoucher
              grupo {
                nombre
              }
              categoria {
                categoria
              }
              producto {
                id
                nombre
              }
            }
            pagos {
              totalPago
              estado
              linkPago
              medioDePago {
                nombre
              }
              nombre
            }
          }
          detalles {
            cantidad
            precio
            producto {
              nombre
              id
              categoriaProducto {
                categoria
              }
            }
          }
        }
      }
    `,
    {
      cachePolicy: "no-cache",
      fetchPolicy: "no-cache",
    }
  );

  useEffect(() => {
    if (open && idPedido) {
      getPedido({
        variables: {
          id: idPedido,
          token: token,
        },
      }).then((res) => {
        setPedido(res?.data?.pedido);
      });
    }
  }, [open]);
  useEffect(() => {
    if (loading) {
      setShowContent(false);
    }
  }, [loading]);

  const [getQr, { loading: loadingQr }] = useLazyQuery(GENERATE_QR, {
    fetchPolicy: "network-only",
  });

  const generarQr = () => {
    setQrError(null);
    getQr({ variables: { token, idPedido } }).then((res) => {
      const datos = res?.data?.generateQR;
      if (!datos?.token) {
        setQr(null);
        setQrError(obtenerErrorQrPedido(res));
        return;
      }
      setQr({
        token: datos.token,
        vigenciaMinutos: datos.vigenciaMinutos,
        venceEn: dayjs().add(datos.vigenciaMinutos, "minute"),
      });
      setQrVencido(false);
    }).catch((err) => {
      setQr(null);
      setQrError(obtenerErrorQrPedido(err));
    });
  };

  useEffect(() => {
    if (!open) {
      setQr(null);
      setQrVencido(false);
      setQrError(null);
    }
  }, [open]);

  // Se genera al abrir el modal o cuando el pedido llega a un estado que lo admite; el
  // mismo QR sirve para los pasos siguientes mientras no venza.
  const habilitaQr = open && String(pedido?.id) === String(idPedido) && puedeGenerarQr(pedido);
  useEffect(() => {
    if (habilitaQr && !qr && !qrError && !loadingQr) generarQr();
  }, [habilitaQr, idPedido]);

  useEffect(() => {
    if (!qr) return;
    const timeoutId = setTimeout(() => setQrVencido(true), Math.max(qr.venceEn.diff(dayjs()), 0));
    return () => clearTimeout(timeoutId);
  }, [qr]);

  const { data: scaneoQR } = useSubscription(
    gql`
      subscription pedido($id: ID!) {
        pedido(id: $id) { id estado updatedAt mensajeSub }
      }
    `,
    {
      variables: {
        id: idPedido,
      },
    }
  );

  useEffect(() => {
    if (scaneoQR && scaneoQR.pedido && scaneoQR.pedido.mensajeSub == null) {
      getPedido({ variables: { id: idPedido, token } }).then((res) => setPedido(res?.data?.pedido));
      setSuccess(true);
      setOpenInfo(true);
    } else if (scaneoQR && scaneoQR.pedido && scaneoQR.pedido.mensajeSub == "ERROR") {
      setError(true);
      setOpenInfo(true);
    }
  }, [scaneoQR]);

  // Subscription a PEDIDO_MODIFICADO solo si el pedido tiene pagos con "Mercado Pago"
  const { data: pedidoModificado } = useSubscription(
    gql`
      subscription pedidoModificado($id: ID!) {
        pedido(id: $id) { id estado updatedAt mensajeSub }
      }
    `,
    {
      variables: {
        id: idPedido,
      },
      skip:
        !idPedido ||
        !pedido?.venta?.pagos?.[0]?.nombre ||
        pedido?.venta?.pagos?.[0]?.nombre !== "Mercado Pago",
    }
  );

  useEffect(() => {
    if (pedidoModificado?.pedido) {
      if (
        pedidoModificado.pedido.estado === "RESERVADO" ||
        pedidoModificado.pedido.estado === "CERRADO" ||
        pedidoModificado.pedido.estado === "ANULADO"
      ) {
        //Cerrar el modal
        handleClose();
        //hacer refetch
        refetch();
      }
    }
  }, [pedidoModificado]);

  const handleCloseAll = () => {
    setSuccess(false);
    setError(false);
    setOpenInfo(false);
  };

  const paymentInfo = getPedidoPaymentInfo(pedido);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={() => {
        handleClose();
      }}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <Fade
            in={loading}
            unmountOnExit
            mountOnEnter
            timeout={{ enter: 600, exit: 400 }}
            style={{ transitionTimingFunction: "ease-in-out" }}
            onExited={() => setShowContent(true)}
          >
            <SoftBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              minHeight="50vh"
            >
              <SoftBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
              >
                <SoftBox
                  component="img"
                  src={logo}
                  alt="UPrO Logo"
                  width="10rem"
                  p={3}
                  sx={{
                    animation: "blinking 1.5s infinite",
                    "@keyframes blinking": {
                      "0%": {
                        opacity: 1,
                      },
                      "50%": {
                        opacity: 0.5,
                      },
                      "100%": {
                        opacity: 1,
                      },
                    },
                  }}
                />
              </SoftBox>
            </SoftBox>
          </Fade>
          {showContent && (
            <Fade
              in={pedido && !loading}
              unmountOnExit
              mountOnEnter
              timeout={{ enter: 600, exit: 400 }}
              style={{ transitionTimingFunction: "ease-in-out" }}
            >
              <SoftBox>
                <SoftBox display="flex" justifyContent="space-between">
                  <SoftBox>
                    <SoftTypography variant="h6">Detalles del pedido</SoftTypography>
                  </SoftBox>
                  <SoftBox display="flex" justifyContent="space-between">
                    <SoftBox>
                      <Tooltip title="Cerrar" placement="top">
                        <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                          close
                        </Icon>
                      </Tooltip>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
                <SoftBox pt={3}>
                  <SoftBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    gap={2}
                  >
                    <Slide in={open} direction="up" mountOnEnter unmountOnExit>
                      <SoftBox>
                        <Card
                          sx={{
                            width: "100%",
                          }}
                        >
                          <SoftBox
                            p={2}
                            bgColor={
                              pedido?.estado === "RESERVADO" &&
                              dayjs(pedido?.fechaPedido).isBefore(dayjs().startOf("day"))
                                ? getUproColor("uproGray")
                                : pedido?.estado === "RESERVADO"
                                ? getUproColor("uproGreen")
                                : pedido?.estado === "EN PREPARACIÓN"
                                ? getUproColor("uproPink")
                                : pedido?.estado === "CERRADO"
                                ? getUproColor("uproGray")
                                : pedido?.estado === "ANULADO"
                                ? getUproColor("uproBlue")
                                : pedido?.estado === "PENDIENTE DE PAGO"
                                ? getUproColor("uproGray")
                                : pedido?.estado === "PAGO EN REVISIÓN"
                                ? getUproColor("uproYellow")
                                : getUproColor("uproPink")
                            }
                          >
                            <SoftBox display="flex" justifyContent="center">
                              <Icon
                                fontSize="medium"
                                sx={{
                                  color: "#fff",
                                  marginRight: 1,
                                  animation: "shakes 1s",
                                  "@keyframes shakes": {
                                    "0%": {
                                      transform: "rotate(0deg)",
                                    },
                                    "25%": {
                                      transform: "rotate(10deg)",
                                    },
                                    "50%": {
                                      transform: "rotate(-20deg)",
                                    },
                                    "75%": {
                                      transform: "rotate(15deg)",
                                    },
                                    "100%": {
                                      transform: "rotate(-5deg)",
                                    },
                                  },
                                }}
                              >
                                {pedido?.estado === "RESERVADO" &&
                                dayjs(pedido?.fechaPedido).isBefore(dayjs().startOf("day"))
                                  ? "history"
                                  : pedido?.estado === "RESERVADO"
                                  ? "schedule"
                                  : pedido?.estado === "EN PREPARACIÓN"
                                  ? "restaurant"
                                  : pedido?.estado === "CERRADO"
                                  ? "check_circle"
                                  : pedido?.estado === "ANULADO"
                                  ? "report"
                                  : pedido?.estado === "PENDIENTE DE PAGO"
                                  ? "hourglass_empty"
                                  : pedido?.estado === "PAGO EN REVISIÓN"
                                  ? "hourglass_empty"
                                  : "restaurant"}
                              </Icon>
                              <SoftTypography
                                variant="h5"
                                fontWeight="bold"
                                textAlign="center"
                                color="white"
                              >
                                {dayjs(pedido?.fechaPedido).isBefore(dayjs().startOf("day")) &&
                                pedido?.estado === "RESERVADO"
                                  ? "Reserva vencida"
                                  : `${makeNiceText(pedido?.estado)}`}
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        </Card>
                      </SoftBox>
                    </Slide>
                    <Slide
                      in={pedido?.estado === "PENDIENTE DE PAGO"}
                      direction="up"
                      mountOnEnter
                      unmountOnExit
                    >
                      {pedido?.venta?.pagos?.length > 0 &&
                      pedido?.venta?.pagos[0]?.nombre == "Mercado Pago" ? (
                        <SoftButton
                          color="uproGreen"
                          circular
                          onClick={() => window.open(pedido?.venta?.pagos[0]?.linkPago, "_blank")}
                        >
                          Ir a Mercado Pago&nbsp;
                          <Icon>payment</Icon>
                        </SoftButton>
                      ) : (
                        localStorage.getItem("idSede") &&
                        localStorage.getItem("idSede") == "1" && (
                          <SoftButton
                            color="uproGreen"
                            circular
                            onClick={() => setOpenModalCargarComprobante(true)}
                          >
                            Cargar comprobante de pago&nbsp;
                            <Icon>file_upload</Icon>
                          </SoftButton>
                        )
                      )}
                    </Slide>
                    <Slide
                      in={open && pedido?.tipo === "Menú"}
                      direction="up"
                      mountOnEnter
                      unmountOnExit
                    >
                      <SoftBox>
                        <Card
                          sx={{
                            width: "100%",
                          }}
                        >
                          <SoftBox p={2}>
                            <SoftBox display="flex" alignItems="center">
                              <SoftBox mr={1} display="flex" alignItems="center">
                                <Icon color="primary">event_available</Icon>
                              </SoftBox>
                              <SoftTypography variant="h6">
                                Turno del pedido&nbsp;
                                <Tooltip
                                  placement="top"
                                  title="Turno en el que solicitaste el pedido."
                                >
                                  <Icon
                                    sx={{
                                      verticalAlign: "middle",
                                    }}
                                    color="secondary"
                                  >
                                    help
                                  </Icon>
                                </Tooltip>
                              </SoftTypography>
                            </SoftBox>
                            <SoftBox mt={2}>
                              <SoftTypography
                                variant="h5"
                                fontWeight="bold"
                                textAlign="center"
                                color="primary"
                              >
                                {`${dayjs(pedido?.horaReserva).format("DD/MM/YYYY HH:mm")}` ||
                                  "Sin turno"}
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        </Card>
                      </SoftBox>
                    </Slide>
                    <Slide in={open} direction="up" mountOnEnter unmountOnExit>
                      <SoftBox>
                        <Card
                          sx={{
                            width: "100%",
                          }}
                        >
                          <SoftBox p={2}>
                            <SoftBox display="flex" alignItems="center">
                              <SoftBox mr={1} display="flex" alignItems="center">
                                <Icon color="primary">access_time</Icon>
                              </SoftBox>
                              <SoftTypography variant="h6">Fecha de creación</SoftTypography>
                            </SoftBox>
                            <SoftBox mt={2}>
                              <SoftTypography
                                variant="h5"
                                fontWeight="bold"
                                textAlign="center"
                                color="primary"
                              >
                                {dayjs(pedido?.createdAt).format("DD/MM/YYYY HH:mm")}
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        </Card>
                      </SoftBox>
                    </Slide>
                    <Slide in={open} direction="up" mountOnEnter unmountOnExit>
                      <SoftBox>
                        <Card
                          sx={{
                            width: "100%",
                          }}
                        >
                          <SoftBox p={2}>
                            <SoftBox display="flex" alignItems="center">
                              <SoftBox mr={1} display="flex" alignItems="center">
                                <Icon color="primary">attach_money</Icon>
                              </SoftBox>
                              <SoftTypography variant="h6">Composición del pago</SoftTypography>
                            </SoftBox>
                            <SoftBox
                              mt={2}
                              role="group"
                              aria-label={`Desglose del pago: ${paymentInfo.etiqueta}`}
                            >
                              <SoftTypography
                                variant="h5"
                                fontWeight="bold"
                                textAlign="center"
                                color="primary"
                              >
                                {paymentInfo.etiqueta}
                              </SoftTypography>

                              {paymentInfo.tieneVoucher && (
                                <SoftBox
                                  mt={1.25}
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="center"
                                  gap={0.75}
                                >
                                  <SoftTypography
                                    variant="caption"
                                    color="secondary"
                                    fontWeight="bold"
                                  >
                                    {getVoucherNombre(pedido?.venta)}
                                  </SoftTypography>
                                  <SoftBox
                                    display="flex"
                                    justifyContent="center"
                                    gap={0.75}
                                    flexWrap="wrap"
                                  >
                                    {getVoucherTipo(pedido?.venta) && (
                                      <SoftBadge
                                        color={
                                          getVoucherTipo(pedido?.venta) === "MONTO"
                                            ? "info"
                                            : "success"
                                        }
                                        badgeContent={getVoucherTipoLabel(pedido?.venta)}
                                      />
                                    )}
                                    {formatVoucherCantidad(pedido?.venta) && (
                                      <SoftBadge
                                        color="primary"
                                        badgeContent={formatVoucherCantidad(pedido?.venta)}
                                      />
                                    )}
                                  </SoftBox>
                                  {getVoucherDestino(pedido?.venta) && (
                                    <SoftTypography variant="caption" color="secondary">
                                      {getVoucherTipoLabel(pedido?.venta)}:{" "}
                                      {getVoucherDestino(pedido?.venta)}
                                    </SoftTypography>
                                  )}
                                </SoftBox>
                              )}

                              <SoftBox
                                mt={2}
                                p={1.5}
                                sx={{
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 2,
                                }}
                              >
                                {[
                                  { label: "Subtotal del pedido", value: paymentInfo.subtotal },
                                  ...(paymentInfo.tieneVoucher
                                    ? [
                                        {
                                          label: "Cubierto por voucher",
                                          value: -paymentInfo.descuento,
                                        },
                                      ]
                                    : []),
                                  { label: "Recargo", value: paymentInfo.recargo },
                                ].map(({ label, value }) => (
                                  <SoftBox
                                    key={label}
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={1}
                                    py={0.35}
                                  >
                                    <SoftTypography variant="caption" color="secondary">
                                      {label}
                                    </SoftTypography>
                                    <SoftTypography variant="caption" fontWeight="bold">
                                      {value < 0
                                        ? "-"
                                        : value > 0 && label === "Recargo"
                                        ? "+"
                                        : ""}
                                      $ {formatMoneyPunto(Math.abs(value))}
                                    </SoftTypography>
                                  </SoftBox>
                                ))}
                                <SoftBox
                                  mt={0.75}
                                  pt={0.75}
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  gap={1}
                                  sx={{ borderTop: "1px solid", borderColor: "divider" }}
                                >
                                  <SoftTypography variant="button" fontWeight="bold">
                                    {paymentInfo.tieneVoucher ? "Remanente del cliente" : "Total"}
                                  </SoftTypography>
                                  <SoftTypography
                                    variant="button"
                                    fontWeight="bold"
                                    color="primary"
                                  >
                                    $ {formatMoneyPunto(paymentInfo.remanente)}
                                  </SoftTypography>
                                </SoftBox>
                              </SoftBox>

                              <SoftBox mt={1.25} display="flex" flexDirection="column" gap={0.75}>
                                {paymentInfo.tieneCuentaCorriente && (
                                  <SoftBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <SoftTypography variant="caption" color="secondary">
                                      <Icon fontSize="small">account_balance_wallet</Icon>
                                      &nbsp;Cuenta corriente
                                    </SoftTypography>
                                    <SoftTypography variant="caption" fontWeight="bold">
                                      $ {formatMoneyPunto(paymentInfo.montoCuentaCorriente)}
                                    </SoftTypography>
                                  </SoftBox>
                                )}
                                {paymentInfo.tieneTransferencia && (
                                  <SoftBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <SoftTypography variant="caption" color="secondary">
                                      <Icon fontSize="small">account_balance</Icon>
                                      &nbsp;Transferencia
                                    </SoftTypography>
                                    <SoftTypography variant="caption" fontWeight="bold">
                                      $ {formatMoneyPunto(paymentInfo.montoTransferencia)}
                                    </SoftTypography>
                                  </SoftBox>
                                )}
                                {paymentInfo.mediosPago
                                  .filter((medio) => !medio.esTransferencia)
                                  .map((medio, index) => (
                                    <SoftBox
                                      key={`${medio.nombre}-${index}`}
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <SoftTypography variant="caption" color="secondary">
                                        <Icon fontSize="small">payments</Icon>&nbsp;{medio.nombre}
                                      </SoftTypography>
                                      <SoftTypography variant="caption" fontWeight="bold">
                                        $ {formatMoneyPunto(medio.monto)}
                                      </SoftTypography>
                                    </SoftBox>
                                  ))}
                                {paymentInfo.tipo === "VOUCHER" && (
                                  <SoftTypography
                                    variant="caption"
                                    color="success"
                                    fontWeight="bold"
                                    textAlign="center"
                                  >
                                    Pedido cubierto completamente con voucher
                                  </SoftTypography>
                                )}
                              </SoftBox>
                            </SoftBox>
                          </SoftBox>
                        </Card>
                      </SoftBox>
                    </Slide>
                    <Slide in={open} direction="up" mountOnEnter unmountOnExit>
                      <SoftBox>
                        <Card
                          sx={{
                            width: "100%",
                          }}
                        >
                          <SoftBox p={2}>
                            <SoftBox display="flex" alignItems="center">
                              <SoftBox mr={1} display="flex" alignItems="center">
                                <Icon color="primary">restaurant_menu</Icon>
                              </SoftBox>
                              <SoftTypography variant="h6">Tipo de pedido&nbsp;</SoftTypography>
                            </SoftBox>
                            <SoftBox mt={2}>
                              <SoftTypography
                                variant="h5"
                                fontWeight="bold"
                                textAlign="center"
                                color="primary"
                              >
                                {pedido?.paraRetirar ? "Para retirar" : "Consumo en el lugar"}
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        </Card>
                      </SoftBox>
                    </Slide>
                    <Slide
                      in={open && pedido?.comentario}
                      direction="up"
                      mountOnEnter
                      unmountOnExit
                    >
                      <SoftBox>
                        <Card
                          sx={{
                            width: "100%",
                          }}
                        >
                          <SoftBox p={2}>
                            <SoftBox display="flex" alignItems="center">
                              <SoftBox mr={1} display="flex" alignItems="center">
                                <Icon color="primary">comment</Icon>
                              </SoftBox>
                              <SoftTypography variant="h6">Comentario&nbsp;</SoftTypography>
                            </SoftBox>
                            <SoftBox mt={2}>
                              <SoftTypography
                                variant="h5"
                                fontWeight="bold"
                                textAlign="center"
                                color="primary"
                              >
                                {makeNiceText(pedido?.comentario)}
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        </Card>
                      </SoftBox>
                    </Slide>
                    <Slide
                      in={
                        open &&
                        pedido?.estado !== "ANULADO" &&
                        pedido?.estado !== "CERRADO" &&
                        dayjs(pedido?.fechaPedido).isBefore(dayjs().endOf("day")) &&
                        pedido?.tipo === "Menú"
                      }
                      direction="up"
                      mountOnEnter
                      unmountOnExit
                    >
                      <SoftBox>
                        <Card
                          sx={{
                            width: "100%",
                          }}
                        >
                          <SoftBox p={2}>
                            <SoftBox display="flex" alignItems="center">
                              <SoftBox mr={1} display="flex" alignItems="center">
                                <Icon color="primary">qr_code</Icon>
                              </SoftBox>
                              <SoftTypography variant="h6">Código QR</SoftTypography>
                            </SoftBox>
                            <SoftBox mt={2} display="flex" justifyContent="center">
                              {esperaHorarioReserva(pedido) ? (
                                <Grid container textAlign={"center"} py={3}>
                                  <Grid item xs={12}>
                                    <Icon sx={{ width: "50px" }}>hourglass_full</Icon>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <SoftTypography
                                      variant="h6"
                                      fontWeight="bold"
                                      textAlign="center"
                                      color="primary"
                                    >
                                      El código se mostrará en el horario de reserva del pedido
                                    </SoftTypography>
                                  </Grid>
                                </Grid>
                              ) : puedeGenerarQr(pedido) ? (
                                <Grid container textAlign={"center"} justifyContent="center">
                                  {qrError?.reservaVencida ? (
                                    <Grid item xs={12}>
                                      <ReservaVencidaAviso mensaje={qrError.mensaje} />
                                    </Grid>
                                  ) : qr && !qrVencido ? (
                                    <>
                                      <Grid item xs={12} display="flex" justifyContent="center">
                                        <QRCode
                                          size={200}
                                          style={{ height: "auto", maxWidth: "200px", width: "100%" }}
                                          value={`pedido/${qr.token}`}
                                          viewBox={`0 0 200 200`}
                                        />
                                      </Grid>
                                      <Grid item xs={12} mt={1}>
                                        <SoftTypography variant="caption" component="p" color="text">
                                          {`El QR es válido por ${
                                            qr.vigenciaMinutos === 60 ? "1 hora" : `${qr.vigenciaMinutos} minutos`
                                          }, hasta las ${qr.venceEn.format("HH:mm")}.`}
                                        </SoftTypography>
                                      </Grid>
                                    </>
                                  ) : (
                                    <>
                                      <Grid item xs={12}>
                                        <Icon sx={{ width: "50px" }}>
                                          {qrError ? "error_outline" : qrVencido ? "timer_off" : "qr_code"}
                                        </Icon>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <SoftTypography variant="h6">
                                          {qrError ? qrError.mensaje : qrVencido ? "El QR venció" : "Generando QR..."}
                                        </SoftTypography>
                                      </Grid>
                                      {(qrError || qrVencido) && (
                                        <Grid item xs={12} mt={1}>
                                          <SoftButton
                                            color="primary"
                                            size="small"
                                            disabled={loadingQr}
                                            onClick={generarQr}
                                          >
                                            {qrVencido ? "Regenerar QR" : "Reintentar"}
                                          </SoftButton>
                                        </Grid>
                                      )}
                                    </>
                                  )}
                                </Grid>
                              ) : (
                                <Grid container textAlign={"center"} py={3}>
                                  <Grid item xs={12}>
                                    <Icon sx={{ width: "50px" }}>
                                      {pedido?.estado === "PENDIENTE DE PAGO"
                                        ? "point_of_sale"
                                        : pedido?.estado === "PAGO EN REVISIÓN"
                                        ? "hourglass_empty"
                                        : "hourglass_empty"}
                                    </Icon>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <SoftTypography variant="h6">
                                      {pedido?.estado === "PENDIENTE DE PAGO"
                                        ? "Pago pendiente"
                                        : pedido?.estado === "PAGO EN REVISIÓN"
                                        ? "Pago en revisión"
                                        : "Esperando retiro"}
                                    </SoftTypography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <SoftTypography variant="caption" component="p" color="text">
                                      {pedido?.estado === "PENDIENTE DE PAGO"
                                        ? "El pedido se encuentra pendiente de pago. El QR se generará una vez que el pago sea confirmado."
                                        : pedido?.estado === "PAGO EN REVISIÓN"
                                        ? "El pedido se encuentra en revisión. El QR se generará una vez que el pago sea confirmado."
                                        : ""}
                                    </SoftTypography>
                                  </Grid>
                                </Grid>
                              )}
                            </SoftBox>
                          </SoftBox>
                        </Card>
                      </SoftBox>
                    </Slide>
                    <Slide in={open} direction="up" mountOnEnter unmountOnExit>
                      <SoftBox
                        sx={{
                          width: "100%",
                        }}
                      >
                        <Card
                          sx={{
                            border: "1px solid rgba(0, 0, 0, 0.12)",
                            zIndex: 2,
                          }}
                        >
                          <SoftBox
                            display="flex"
                            justifyContent="space-between"
                            px={2}
                            py={1}
                            sx={{
                              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                            }}
                          >
                            <SoftBox display="flex" alignItems="center">
                              <SoftBox mr={1} display="flex" alignItems="center">
                                <Icon color="primary">shopping_cart</Icon>
                              </SoftBox>
                              <SoftTypography variant="h6" fontWeight="bold">
                                Productos
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                          <Collapse
                            in={open}
                            sx={{
                              maxHeight: {
                                xs: "100%",
                                sm: "50vh",
                              },
                              overflowY: "auto",
                            }}
                          >
                            {pedido?.detalles?.map((item, index) => (
                              <Slide
                                in={open}
                                key={index}
                                direction="up"
                                mountOnEnter
                                unmountOnExit
                              >
                                <SoftBox>
                                  <SoftBox
                                    px={2}
                                    py={1}
                                    sx={{
                                      borderBottom:
                                        index === pedido?.detalles?.length - 1
                                          ? "none"
                                          : "1px solid rgba(0, 0, 0, 0.12)",
                                      "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                                      },
                                    }}
                                  >
                                    <SoftBox display="flex" justifyContent="space-between" gap={2}>
                                      <SoftBox display="flex" alignItems="center" gap={1}>
                                        <SoftBox>
                                          <Tooltip
                                            title={item?.producto?.categoriaProducto?.categoria}
                                            placement="top"
                                          >
                                            <Icon
                                              sx={{
                                                color: getUproColor("uproYellow"),
                                                cursor: "pointer",
                                              }}
                                            >
                                              restaurant_menu
                                            </Icon>
                                          </Tooltip>
                                        </SoftBox>
                                        <SoftBox display="flex" gap={1} alignItems="center">
                                          <SoftTypography variant="h6">
                                            {makeNiceText(item?.producto?.nombre)}
                                          </SoftTypography>
                                          <SoftBadge
                                            color="uproYellow"
                                            badgeContent={`x${item?.cantidad}`}
                                          />
                                        </SoftBox>
                                      </SoftBox>
                                      <SoftBox
                                        display="flex"
                                        justifyContent="flex-end"
                                        alignItems="center"
                                      >
                                        <SoftBox
                                          display="flex"
                                          flexDirection="column"
                                          alignItems="flex-end"
                                        >
                                          <SoftTypography
                                            variant="h6"
                                            color={"text"}
                                            sx={{
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            $ {formatMoneyPunto(item?.precio * item?.cantidad)}
                                          </SoftTypography>
                                          <SoftTypography
                                            variant="caption"
                                            color={"text"}
                                            sx={{
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            $ {formatMoneyPunto(item?.precio)} c/u
                                          </SoftTypography>
                                        </SoftBox>
                                      </SoftBox>
                                    </SoftBox>
                                  </SoftBox>
                                </SoftBox>
                              </Slide>
                            ))}
                          </Collapse>
                        </Card>
                        <Card
                          sx={{
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            position: "sticky",
                            marginTop: -2,
                            zIndex: 1,
                          }}
                        >
                          <SoftBox bgColor="primary">
                            <SoftBox
                              display="flex"
                              justifyContent="center"
                              bgColor="primary"
                              p={2}
                              pt={4}
                            >
                              <SoftTypography variant="h6" fontWeight="bold" color="white">
                                Total: $ {formatMoneyPunto(pedido?.total)}
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        </Card>
                      </SoftBox>
                    </Slide>
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Fade>
          )}
          <ModalInfo
            open={openInfo}
            handleCloseAll={() => {
              handleCloseAll();
            }}
            error={error}
            success={success}
          />
          <ModalCargarComprobante
            open={openModalCargarComprobante}
            handleClose={() => {
              setOpenModalCargarComprobante(false);
            }}
            refetch={() => {
              getPedido({
                variables: {
                  id: idPedido,
                  token: token,
                },
              }).then((res) => {
                setPedido(res?.data?.pedido);
                refetch();
              });
            }}
            pedido={pedido}
            token={token}
          />
        </Card>
      </Fade>
    </Modal>
  );
}

ModalDetallesPedido.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idPedido: PropTypes.string,
  token: PropTypes.string,
  refetch: PropTypes.func,
};
